import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import Section from '@components/Section'

import useStudents, { type StudentData } from '@/hooks/useStudents'
import AddOrEditStudentForm from '@components/AddOrEditStudentForm'
import { sendStudentAddedEvent, sendStudentAddErrorEvent } from '@/services/analytics/gtm'
import { formatAddStudentError } from '@/util/formatters'

export default function AddStudentPage() {
  const { t } = useTranslation()
  const { students, createStudent } = useStudents()
  const navigate = useNavigate()

  async function handleAddStudent(data: StudentData) {
    try {
      await createStudent({
        student: {
          firstName: data.firstName,
          lastName: data.lastName,
          classroomId: data.classroomId,
        },
      })
      sendStudentAddedEvent({
        isOnboarding: false,
        studentCount: (students?.length ?? 0) + 1,
      })
      toast.success(t('content.add_student_page.add_success_toast'))
      navigate('../account', { replace: true })
      return true
    } catch (error) {
      sendStudentAddErrorEvent({ isOnboarding: false })
      const rawErrorMessage =
        error instanceof Error
          ? error.message
          : t('content.components.add_or_edit_student.generic_error_toast')
      const formattedMessage = formatAddStudentError(rawErrorMessage, t)
      Sentry.logger.error('Error creating student', { error: String(error) })
      toast.error(formattedMessage, {
        autoClose: false,
      })
      return false
    }
  }

  return (
    <>
      <title>{t('content.add_student_page.page_title')}</title>
      <Section title={t('content.add_student_page.section_title')} wrapperStyle='mt-8 mb-4'>
        <div className='text-2xl font-bold text-white text-center'>{t('content.add_student_page.header')}</div>
        <AddOrEditStudentForm onSubmit={handleAddStudent} />
      </Section>
    </>
  )
}
